import {
  ActionIcon,
  Alert,
  Anchor,
  Box,
  getDefaultZIndex,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { IconArrowBackUp, IconInfoCircle } from '@tabler/icons-react'
import { type ReactNode, useMemo } from 'react'

import { Table } from '@/components/Table'
import { theme } from '@/configs/theme'
import * as classes from '@/styles/BulkUploadWizard.css'
import {
  type BulkDefaults,
  type BulkUploadMode,
  canStagedFileUseMedia,
  type DefaultField,
  defaultModeFor,
  fileTypeLabel,
  formatFileSize,
  isCustomized,
  pickDeliveryMode,
  pickSidekick,
  resolvedDeliveryMode,
  resolvedSidekick,
  skipsMediaDefault,
  type StagedFile,
  titleError,
} from '@/utils/bulkContentUpload'
import { isMediaModeName, MEDIA_BARRED_TOOLTIP } from '@/utils/deliveryModes'
import type { sidekickSelectOptions } from '@/utils/sidekicks'
import type { TableColumnDef } from '@/utils/table'

import {
  allRemovedText,
  bulkUploadCopy,
  byFileTypeLabel,
  customizedSummary,
  mediaSkippedTooltip,
  modeDefaultLabel,
  resetAllLabel,
  resetToDefaultLabel,
  sidekickDefaultLabel,
} from './bulkUploadCopy'

export type DeliveryModeOption = { value: string; label: string }

/** Cells a default change just moved; `token` restarts the highlight when the same cells move again. */
export type Highlight = { field: DefaultField; names: Set<string>; token: number }

type Patch = Partial<Pick<StagedFile, 'title' | 'eskId' | 'deliveryModeName'>>

type ReviewStepProps = {
  mode: BulkUploadMode
  files: StagedFile[]
  defaults: BulkDefaults
  sidekickOptions: ReturnType<typeof sidekickSelectOptions>
  sidekickLabels: Map<string, string>
  deliveryModes: DeliveryModeOption[]
  modeLabels: Map<string, string>
  deliveryModesFailed: boolean
  /** What the last default change did, announced to screen readers. */
  status: string
  highlight: Highlight | undefined
  onChange: (name: string, patch: Patch) => void
  onRemove: (name: string) => void
  onDefaultChange: (field: DefaultField, value: string | null) => void
  onResetAll: () => void
}

const BY_FILE_TYPE = '__by-file-type__'

export function ReviewStep(props: ReviewStepProps) {
  const { mode, files, defaults, sidekickOptions, sidekickLabels, deliveryModes, modeLabels } = props
  const { deliveryModesFailed, status, highlight, onChange, onRemove, onDefaultChange, onResetAll } = props
  const copy = bulkUploadCopy[mode]
  const isModules = mode === 'modules'
  const customized = isModules ? files.filter(isCustomized).length : 0

  // The shared Table virtualizes its rows, which a batch of hundreds of files needs.
  const columns = useMemo<TableColumnDef<StagedFile>[]>(() => {
    const styleNames = deliveryModes.map(m => m.value)
    // The wrapper is always there, so a highlight coming or going never remounts the dropdown inside.
    const highlighted = (staged: StagedFile, field: DefaultField, cell: ReactNode) => (
      <Box
        className={
          highlight?.field === field && highlight.names.has(staged.file.name)
            ? highlight.token % 2
              ? classes.highlightOdd
              : classes.highlightEven
            : undefined
        }>
        {cell}
      </Box>
    )

    const fileColumns: TableColumnDef<StagedFile>[] = [
      {
        id: 'fileName',
        header: 'File Name',
        size: 180,
        cell: ({ row }) => (
          <Stack gap={0} miw={0}>
            <Text size="sm" truncate="end">
              {row.original.file.name}
            </Text>
            <Text size="xs" c="dimmed">
              {formatFileSize(row.original.file.size)}
            </Text>
          </Stack>
        ),
      },
      {
        id: 'fileType',
        header: 'File Type',
        size: 96,
        meta: { grow: false },
        cell: ({ row }) => <Text size="sm">{fileTypeLabel(row.original.file.name)}</Text>,
      },
      {
        id: 'title',
        header: copy.titleColumn,
        size: 240,
        cell: ({ row }) => (
          <TextInput
            size="xs"
            w="100%"
            aria-label={`${copy.titleColumn} for ${row.original.file.name}`}
            value={row.original.title}
            error={titleError(row.original.title)}
            onChange={e => onChange(row.original.file.name, { title: e.currentTarget.value })}
          />
        ),
      },
    ]
    if (!isModules) return fileColumns

    return [
      ...fileColumns,
      {
        id: 'sidekick',
        header: 'Sidekick',
        size: 204,
        meta: { grow: false },
        cell: ({ row }) => {
          const staged = row.original
          const own = staged.eskId !== null
          return highlighted(
            staged,
            'eskId',
            <Group gap={4} wrap="nowrap">
              <Select
                size="xs"
                aria-label={`Sidekick for ${staged.file.name}`}
                data={sidekickOptions}
                value={resolvedSidekick(staged, defaults)}
                allowDeselect={false}
                classNames={own ? { input: classes.customizedInput } : undefined}
                onChange={value => value && onChange(staged.file.name, pickSidekick(staged, value, defaults))}
              />
              <Box className={classes.cellMarker}>
                {own && (
                  <ResetButton
                    label={resetToDefaultLabel(sidekickLabels.get(defaults.eskId) ?? defaults.eskId)}
                    ariaLabel={`Use the default Sidekick for ${staged.file.name}`}
                    onClick={() => onChange(staged.file.name, { eskId: null })}
                  />
                )}
              </Box>
            </Group>,
          )
        },
      },
      {
        id: 'deliveryMode',
        header: 'Delivery Mode',
        size: 188,
        meta: { grow: false },
        cell: ({ row }) => {
          const staged = row.original
          const own = staged.deliveryModeName !== null
          const mediaAllowed = canStagedFileUseMedia(staged)
          const followed = defaultModeFor(staged, styleNames, defaults)
          return highlighted(
            staged,
            'deliveryModeName',
            <Group gap={4} wrap="nowrap">
              <Select
                size="xs"
                aria-label={`Delivery mode for ${staged.file.name}`}
                data={deliveryModes.map(m => ({ ...m, disabled: isMediaModeName(m.value) && !mediaAllowed }))}
                value={resolvedDeliveryMode(staged, styleNames, defaults) ?? null}
                allowDeselect={false}
                classNames={own ? { input: classes.customizedInput } : undefined}
                onChange={value =>
                  value && onChange(staged.file.name, pickDeliveryMode(staged, value, styleNames, defaults))
                }
                renderOption={({ option }) =>
                  option.disabled ? (
                    <Tooltip
                      label={MEDIA_BARRED_TOOLTIP}
                      position="right"
                      multiline
                      w={theme.rem(240)}
                      zIndex={getDefaultZIndex('max')}>
                      <Text size="xs">{option.label}</Text>
                    </Tooltip>
                  ) : (
                    <Text size="xs">{option.label}</Text>
                  )
                }
              />
              <Box className={classes.cellMarker}>
                {own ? (
                  <ResetButton
                    label={resetToDefaultLabel((followed && modeLabels.get(followed)) ?? byFileTypeLabel)}
                    ariaLabel={`Use the default delivery mode for ${staged.file.name}`}
                    onClick={() => onChange(staged.file.name, { deliveryModeName: null })}
                  />
                ) : (
                  skipsMediaDefault(staged, defaults) && (
                    <Tooltip
                      label={mediaSkippedTooltip}
                      multiline
                      w={theme.rem(240)}
                      withArrow
                      events={{ hover: true, focus: true, touch: false }}>
                      <span tabIndex={0} role="img" aria-label={mediaSkippedTooltip} className={classes.markerIcon}>
                        <IconInfoCircle size={16} aria-hidden="true" />
                      </span>
                    </Tooltip>
                  )
                )}
              </Box>
            </Group>,
          )
        },
      },
    ]
  }, [
    copy.titleColumn,
    isModules,
    sidekickOptions,
    sidekickLabels,
    deliveryModes,
    modeLabels,
    defaults,
    highlight,
    onChange,
  ])

  return (
    <Stack gap="md">
      <Text size="sm">{copy.reviewSubtitle}</Text>

      {isModules && (
        <Stack gap={6}>
          <Group gap="xl" wrap="wrap">
            <Group gap="xs" wrap="nowrap">
              <Text size="sm" fw={500}>
                {sidekickDefaultLabel}:
              </Text>
              <Select
                aria-label={sidekickDefaultLabel}
                size="xs"
                data={sidekickOptions}
                value={defaults.eskId}
                allowDeselect={false}
                onChange={value => value && onDefaultChange('eskId', value)}
                w={theme.rem(200)}
              />
            </Group>
            <Group gap="xs" wrap="nowrap">
              <Text size="sm" fw={500}>
                {modeDefaultLabel}:
              </Text>
              <Select
                aria-label={modeDefaultLabel}
                size="xs"
                data={[{ value: BY_FILE_TYPE, label: byFileTypeLabel }, ...deliveryModes]}
                value={defaults.deliveryModeName ?? BY_FILE_TYPE}
                allowDeselect={false}
                onChange={value => value && onDefaultChange('deliveryModeName', value === BY_FILE_TYPE ? null : value)}
                w={theme.rem(180)}
              />
            </Group>
          </Group>
          <Group gap="md" justify="space-between" wrap="nowrap" mih={theme.rem(20)}>
            <Text size="xs" c="dimmed" role="status" aria-live="polite" aria-atomic="true">
              {status}
            </Text>
            {customized > 0 && (
              <Text size="xs" className={classes.customizedSummary}>
                {customizedSummary(customized)} ·{' '}
                <Anchor component="button" type="button" size="xs" onClick={onResetAll}>
                  {resetAllLabel}
                </Anchor>
              </Text>
            )}
          </Group>
        </Stack>
      )}

      {isModules && deliveryModesFailed && (
        <Text c="red" size="sm">
          Delivery modes couldn&rsquo;t be loaded. Reload the page to try again.
        </Text>
      )}

      {files.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="xl">
          {allRemovedText}
        </Text>
      ) : (
        <Table<StagedFile>
          data={files}
          columns={columns}
          getRowId={row => row.file.name}
          enableTopToolbar={false}
          enableGlobalFilter={false}
          enableRowVirtualization
          enableStickyHeader
          density="xs"
          containerProps={{ style: { maxHeight: '45vh' } }}
          rowActionsInclude={['delete']}
          rowActionsTooltipLabels={{ delete: 'Remove File' }}
          onRowActionClick={(_, row) => onRemove(row.original.file.name)}
          actionsColumnSize={64}
        />
      )}

      {copy.reviewNote && (
        <Alert color="indigo" variant="light" icon={<IconInfoCircle />}>
          <Text size="sm">{copy.reviewNote}</Text>
        </Alert>
      )}
    </Stack>
  )
}

function ResetButton({ label, ariaLabel, onClick }: { label: string; ariaLabel: string; onClick: () => void }) {
  return (
    <Tooltip label={label} withArrow>
      <ActionIcon variant="subtle" size="sm" aria-label={ariaLabel} onClick={onClick}>
        <IconArrowBackUp size={16} aria-hidden="true" />
      </ActionIcon>
    </Tooltip>
  )
}
